/**
 * Wordings of «Учитель» v3 — family «opponent» (docs/TEACHING-V3.md §5; pool specs: ../spec.ts).
 * Rules: no Latin, NO squares (the board shows them), Гамбитик speaks of himself as a boy, the child through {g:…},
 * no «молодец», no clock words; «вот эти клетки / этот фланг / сюда» only in pools with a board cue.
 *
 * Truth notes for editors:
 *  - `v3.opp.*` only describe the opponent's move; `v3.opp.quiet` never promises «ничего опасного»;
 *    `v3.opp.earlyQueen` is «ферзь вышел до 10-го хода, пока минимум две лёгкие фигуры дома» — not «все фигуры дома»;
 *  - `v3.danger.hanging.undefended` may say «без защиты», `…attacked` must not (the piece IS defended, but loses —
 *    by a cheaper attacker OR by more attackers, so no «нападает сильнее» and no «одной защиты мало»);
 *  - `v3.danger.*` never say «спасаем {коня}»: a `v3.danger.letGo.*` line may follow;
 *  - `v3.danger.letGo.*` have no subject: the piece to leave is never named (it may be a pawn on stages 3–5);
 *  - `v3.treasure.free` = без защиты / бесплатно; `…win` = выгодно (never «без защиты»); `…mate` is a mate in 1–3,
 *    so no «в один ход»; the treasure line never tells the move — `v3.treasure.ask` follows it;
 *  - stage words: «вилка» from stage 2 (stage 1: «двойной удар»), «слабая пешка» and «ранний ферзь» from stage 2,
 *    «фланг» from stage 3.
 *  - `v3.danger.letGo.*` follow a hanging, fork or threat line (never a check or a mate threat) and precede the advice,
 *    so they speak of «спасать / защищаться / отвечать на угрозу» in general; `v3.treasure.ask` follows every treasure
 *    line, `hunt` included, so it never names what the treasure is.
 *  - `v3.danger.lastStand` is the advice's tail when the mate cannot be stopped (after the mate line, or alone in
 *    blitz): the next move loses whatever we play, so it never promises a rescue, safety or «ход сильнее» — only that
 *    we play on to the end; no piece named (it follows any lead).
 *  - `v3.danger.letGo.*` are banded over stages 1–5 and may be said alone (blitz, or the danger line dropped for
 *    length — the board keeps the danger's highlight), so each wording stands on its own. Stages 1–2 get the plainest
 *    words («спасать», «шах», «ход сильнее / важнее»), stages 3–5 «угроза», «идея», «план», «ответим», «в запасе».
 *    A tight reply falls back to the shortest wordings of the stage: keep at least three of the shortest length.
 *
 * Openers (the ear hears the start; the first 50-game run heard «{Конь} соперника …» 5 times and «Соперник может
 * съесть …» 3 times in one game): in every pool no first word and no two-word opening starts more than a quarter of
 * the wordings. The danger pools are heard up to ten times a game, so across them an interjection («Ой», «Внимание»,
 * «Берегись», «Стоп» …) opens at most 3 wordings, and «Осторожно», «Соперник может», «{Твой} {конь}», «{Конь}
 * соперника» at most one wording per pool. Vary the build, not only the first word: the piece first («{Коня} у нас
 * никто не сторожит…»), a place first («Под ударом и без охраны — …»), «Раз — и …», the manner first («С выгодой
 * для себя соперник …»).
 */
import type { LessonWordings } from '../types.ts';

export const WORDINGS: LessonWordings = {
  // ───────────────────────── the opponent's move (without squares; the board shows it) ─────────────────────────

  'v3.opp.develop': [
    { t: 'Соперник вывел {коня}.' },
    { t: '{Конь} соперника {p:вышел|вышла} из дома.' },
    { t: 'Ага, соперник тоже выводит фигуры.' },
    { t: 'Смотри-ка, {конь} соперника уже в пути.' },
    { t: 'Так-так, {конь} соперника {p:отправился|отправилась} в поход.' },
    { t: 'Соперник не дремлет: {конь} уже в деле.' },
    { t: 'Вот и {конь} соперника в игре.' },
    { t: 'В бой вступает {конь} соперника.' },
    { t: 'Теперь и {конь} соперника {p:готов|готова} к бою.' },
    { t: 'Армия соперника просыпается — {p:вышел|вышла} {конь}.' },
    { t: 'У соперника новый боец — {конь}.' },
  ],

  'v3.opp.centerPawn': [
    { t: 'Соперник поставил пешку в центр.' },
    { t: 'Пешка соперника шагнула в центр.' },
    { t: 'Тоже к центру — соперник не отстаёт!' },
    { t: 'Кусочек центра соперник занял пешкой.' },
    { t: 'Соперник спорит с нами за центр.' },
    { t: 'В центр пришла пешка соперника.' },
    { t: 'Центр нужен и сопернику — его пешка уже там.' },
    { t: 'Так-так, за центр берётся и соперник.' },
    { t: 'Вот эти клетки — центр, и туда встала пешка соперника.' },
    { t: 'Смотри сюда: в центре теперь пешка соперника.' },
  ],

  'v3.opp.capture': [
    { t: 'Соперник съел {твоего} {коня}.' },
    { t: 'Ой, у нас съели {коня}.' },
    { t: 'Соперник забрал {твоего} {коня}.' },
    { t: 'Эх, {твой} {конь} {p:ушёл|ушла} с доски.' },
    { t: 'Хрум — и соперник съел {коня}.' },
    { t: 'Ой-ой, {твоего} {коня} забрали.' },
    { t: '{Коня} съели, но игра продолжается.' },
    { t: 'Минус {конь}: соперник {его} съел.' },
    { t: 'Так бывает: соперник съел {коня}.' },
  ],

  'v3.opp.recapture': [
    { t: 'Соперник забрал обратно — это размен.' },
    { t: 'Соперник тут же съел в ответ.' },
    { t: 'Мы съели, соперник съел — размен.' },
    { t: 'Размен: соперник взял в ответ.' },
    { t: 'Меняемся: соперник ответил тем же.' },
    { t: 'Ага, взятие в ответ — получился размен.' },
    { t: 'Вот и поменялись: соперник тоже взял.' },
    { t: 'Раз — мы, два — соперник: вот и размен.' },
  ],

  'v3.opp.attack': [
    { t: 'Соперник напал на {твоего} {коня}.' },
    { t: 'Ой, на {твоего} {коня} напали!' },
    { t: 'Теперь {твой} {конь} под ударом.' },
    { t: 'Ух, замахнулись на {твоего} {коня}!' },
    { t: 'Похоже, {твоего} {коня} хотят съесть.' },
    { t: 'У соперника новая цель — {твой} {конь}.' },
    { t: 'Смотри: {твоего} {коня} атакуют!' },
    { t: 'Соперник уже смотрит на {твоего} {коня}.' },
    { t: 'Вот сюда целится соперник — в {твоего} {коня}.' },
    { t: 'На {твоего} {коня} пошла атака.' },
    { t: 'Ого, соперник перешёл в нападение!' },
  ],

  'v3.opp.check': [
    { t: 'Соперник объявил шах.' },
    { t: 'Шах от соперника!' },
    { t: 'Нашему королю объявили шах.' },
    { t: 'Ой, соперник дал шах!' },
    { t: 'Наш король попал под шах.' },
    { t: 'Это шах — соперник атакует нашего короля.' },
    { t: 'Соперник решил пошуметь — шах!' },
    { t: 'Бах — и шах нашему королю!' },
    { t: 'У нас шах — король под ударом.' },
  ],

  'v3.opp.castle': [
    { t: 'Соперник сделал рокировку.' },
    { t: 'Король соперника спрятался в домик.' },
    { t: 'Одним ходом соперник спрятал короля и вывел ладью.' },
    { t: 'Ага, соперник позаботился о своём короле.' },
    { t: 'Хоп — и король соперника уже спрятан.' },
    { t: 'Так-так, у соперника рокировка.' },
    { t: 'Домик для короля готов — соперник рокировался.' },
    { t: 'Соперник убрал короля подальше от центра.' },
    { t: 'Король соперника ушёл на фланг — это рокировка.', stages: [3, 5] },
  ],

  'v3.opp.earlyQueen': [
    { t: 'Соперник рано вывел ферзя.' },
    { t: 'Ого, ферзь соперника уже тут как тут!' },
    { t: 'Рановато ферзь соперника вышел в бой.' },
    { t: 'Соперник поторопился с ферзём — его можно погонять!' },
    { t: 'Ферзь соперника уже гуляет по доске, а игра только началась.' },
    { t: 'Ранний ферзь у соперника: будем нападать на него и выводить фигуры.', stages: [2, 5] },
    { t: 'Вот этот ферзь соперника выскочил слишком рано.' },
    { t: 'Хм, соперник сразу вывел самую сильную фигуру.' },
    { t: 'Так-так, соперник уже играет ферзём.' },
  ],

  'v3.opp.threatMate': [
    { t: 'Соперник грозит нашему королю матом!' },
    { t: 'У соперника угроза мата.' },
    { t: 'Соперник хочет поставить мат нашему королю!' },
    { t: 'Ой-ой, у соперника в планах мат!' },
    { t: 'Вижу, соперник задумал мат.' },
    { t: 'Ого, соперник нацелился на мат!' },
    { t: 'Тревога: если мы не защитимся, соперник поставит мат!' },
    { t: 'Мат нашему королю — вот что задумал соперник!' },
    { t: 'Кажется, соперник мечтает о мате.' },
    { t: 'Над нашим королём тучи — у соперника есть идея мата.' },
  ],

  'v3.opp.aimWeak': [
    { t: 'Соперник целится в пешку рядом с нашим королём.' },
    { t: 'К пешке возле нашего короля присматривается соперник.' },
    { t: 'Под прицелом соперника — пешка у нашего короля.' },
    { t: 'Соперник метит в пешку, которую охраняет наш король.' },
    { t: 'Смотри, соперник подбирается к пешке-соседке нашего короля.' },
    { t: 'Кажется, соперник нашёл слабое место у нашего короля.' },
    { t: 'Наша слабая пешка приглянулась сопернику.', stages: [2, 5] },
    { t: 'Слабая пешка у нашего короля — вот куда смотрит соперник.', stages: [2, 5] },
    { t: 'Вот эта пешка у нашего короля — сюда целится соперник.' },
  ],

  'v3.opp.trade': [
    { t: 'Соперник предлагает размен.' },
    { t: 'Хм, соперник хочет разменяться.' },
    { t: 'Кажется, нас зовут на размен.' },
    { t: 'Поменяться соперник не против.' },
    { t: 'Похоже, соперник ищет размен.' },
    { t: 'Соперник как будто говорит: давай меняться!' },
    { t: 'Размен на подходе — решать нам, меняться или нет.' },
    { t: 'Вижу, соперник готов меняться.' },
  ],

  'v3.opp.quiet': [
    { t: 'Соперник сделал тихий ход.' },
    { t: 'Тихий ход — соперник готовится.' },
    { t: 'Тихо, как мышка, сходил соперник.' },
    { t: 'Ход спокойный, но мы всё равно смотрим в оба.' },
    { t: 'Соперник не спешит: ход без шума.' },
    { t: 'Спокойный ход соперника — он что-то строит.' },
    { t: 'Ага, соперник думает о своём.' },
    { t: 'Так-так, соперник копит силы.' },
    { t: 'Пока соперник ходит спокойно.' },
  ],

  // ───────────────────────── danger (said at once, the red highlight is on the board) ─────────────────────────

  'v3.danger.check': [
    { t: 'Шах — сначала спасаем короля!' },
    { t: 'Нашему королю шах — выручаем его!' },
    { t: 'Сначала король: ему объявили шах!' },
    { t: 'Королю шах, остальное потом.' },
    { t: 'Король под шахом — сперва думаем о нём!' },
    { t: 'Внимание, шах: король прежде всего!' },
    { t: 'Шах нашему королю — спасаем его первым делом.' },
    { t: 'Тревога, королю шах — сейчас главное спасти его!' },
    { t: 'Стоп, шах — всё остальное подождёт!' },
    { t: 'Все дела потом — у нас шах!' },
  ],

  'v3.danger.mate': [
    { t: 'Соперник грозит матом — сначала защищаемся!' },
    { t: 'У соперника угроза мата — защищаем короля!' },
    { t: 'Сейчас главное — спасти короля от мата.' },
    { t: 'Ой-ой, пахнет матом — сначала оборона!' },
    { t: 'Мат на подходе — пора обороняться!' },
    { t: 'Нашему королю грозит мат — все силы на защиту!' },
    { t: 'Эй, соперник готовит мат — надо помочь королю!' },
    { t: 'Берегись, мат близко — защищаемся первым делом!' },
    { t: 'Сначала защита — от мата надо спасаться!' },
    { t: 'Опасно: ещё чуть-чуть — и соперник поставит мат!' },
  ],

  'v3.danger.hanging.undefended': [
    { t: 'Ой, {твоего} {коня} могут съесть — {он} без защиты!' },
    { t: 'Ай, {твой} {конь} {p:один-одинёшенек|одна-одинёшенька} и под ударом!' },
    { t: 'Беда: на {твоего} {коня} напали, а защиты нет!' },
    { t: 'Глянь сюда: {твой} {конь} под ударом и без защиты!' },
    { t: 'Ой-ой, {твоего} {коня} могут забрать даром!' },
    { t: 'Скорее смотри: {твоего} {коня} могут съесть бесплатно!' },
    { t: 'Соперник может съесть {твоего} {коня} просто так!' },
    { t: '{Коня} у нас никто не сторожит, а соперник уже прицелился!' },
    { t: 'Раз — и {твоего} {коня} съедят: охраны-то нет!' },
    { t: 'У {коня:gen} нет защиты, а соперник уже напал!' },
    { t: 'Под ударом и без охраны — {твой} {конь}!' },
    { t: 'Ох, {твоего} {коня} могут утащить — никто не мешает!' },
    { t: 'Опа, {твой} {конь} {p:остался|осталась} без прикрытия — и под ударом!' },
    { t: 'Осторожно: {твой} {конь} под ударом, и никто {его} не прикрывает!' },
    { t: 'Эй, за {твоего} {коня} некому заступиться, а {его} атакуют!' },
    { t: 'Тревога: {твой} {конь} — лёгкая добыча для соперника!' },
    { t: '{Твой} {конь} без защиты, и {его} хотят съесть!' },
  ],

  'v3.danger.hanging.attacked': [
    { t: 'Ух, на {твоего} {коня} напали, и защита не поможет!' },
    { t: '{Твой} {конь} под ударом — защита {его} не выручит.' },
    { t: 'Ой, {твоего} {коня} могут выгодно съесть, хоть {его} и защищают!' },
    { t: 'С выгодой для себя соперник может съесть {твоего} {коня}!' },
    { t: 'Берегись: {твоего} {коня} атакуют, и защиты не хватает!' },
    { t: 'Внимание, {твой} {конь} в опасности, даже с защитой!' },
    { t: 'Тут опасно: {твоего} {коня} могут съесть, и размен нам невыгоден.' },
    { t: 'Можем потерять {коня} — соперник заберёт больше, чем отдаст.' },
    { t: 'Смотри сюда: {твоего} {коня} защищают, но этого мало!' },
    { t: 'Хоть защита и есть, {твоего} {коня} могут отнять!' },
    { t: '{Коня} могут забрать, и после размена мы окажемся в минусе!' },
    { t: 'Эх, {твоего} {коня} охраняют, но размен выйдет в пользу соперника!' },
    { t: 'Похоже, {твой} {конь} в беде: защита есть, а толку мало.' },
    { t: 'Опасный момент: за {коня} заступятся, но мы всё равно потеряем больше!' },
    { t: 'Стоп: {коня} защищают, но соперник всё равно останется в плюсе!' },
  ],

  'v3.danger.fork': [
    { t: 'Хм, соперник готовит двойной удар!' },
    { t: 'Берегись, соперник хочет напасть сразу на двоих!' },
    { t: 'Соперник задумал ударить сразу в две стороны!' },
    { t: 'Гляди: у соперника есть ход, который бьёт сразу по двоим!' },
    { t: 'Сразу двоих наших — вот куда метит соперник!' },
    { t: 'Хитро: одним ходом соперник может достать двоих!' },
    { t: 'Вилка на подходе — берегись!', stages: [2, 5] },
    { t: 'Соперник готовит вилку — надо быть начеку!', stages: [2, 5] },
    { t: 'Ой-ой, у соперника вилка наготове!', stages: [2, 5] },
  ],

  'v3.danger.threat': [
    { t: 'Осторожно: соперник что-то задумал!' },
    { t: 'Хм, у соперника появилась хитрая угроза!' },
    { t: 'Внимание: соперник затевает неприятность!' },
    { t: 'Погоди-ка, соперник кое-что приготовил!' },
    { t: 'Соперник хитрит — давай посмотрим внимательнее.' },
    { t: 'Ой, у соперника есть угроза!' },
    { t: 'Смотри сюда: соперник готовит удар!' },
    { t: 'Чувствую подвох: соперник припас сюрприз!' },
  ],

  'v3.danger.letGo.check': [
    // stages 1–2: the plainest words (спасать, шах, король), 5–6 words — three of 5, so a tight reply still has a choice
    { t: 'Даём шах — это важнее спасения!', stages: [1, 2] },
    { t: 'Спасать некогда — у нас шах!', stages: [1, 2] },
    { t: 'Сначала шах, спасать не надо!', stages: [1, 2] },
    { t: 'Можно не спасать: мы дадим шах!', stages: [1, 2] },
    { t: 'Шах королю соперника — вот что важнее!', stages: [1, 2] },
    // every stage
    { t: 'Можно не убегать: сначала шах, и соперник будет спасать короля.' },
    { t: 'Мы дадим шах — пусть соперник спасает своего короля!' },
    { t: 'Убегать не обязательно — сначала объявим шах!' },
    { t: 'Сейчас можно не прятаться: у нас шах!' },
    { t: 'Шах важнее: соперник будет занят своим королём.' },
    { t: 'Не торопимся спасаться: сперва шах королю соперника!' },
    { t: 'Королю соперника — шах, и ему придётся спасаться!' },
    { t: 'Защищаться будем потом, а пока — шах!' },
    // stages 3–5: «ответим», «в запасе», «выкручивается», «не до нас» — the older child's words
    { t: 'Со спасением подождём — у нас есть шах!', stages: [3, 5] },
    { t: 'Есть кое-что поинтереснее спасения — шах!', stages: [3, 5] },
    { t: 'Ответим по-другому — шахом!', stages: [3, 5] },
    { t: 'А мы в ответ объявим шах — сопернику станет не до нас!', stages: [3, 5] },
    { t: 'Хитрый ход: сначала шах, пусть соперник выкручивается!', stages: [3, 5] },
    { t: 'Беда подождёт — у нас в запасе шах!', stages: [3, 5] },
    { t: 'Вместо спасения — шах!', stages: [3, 5] },
    { t: 'Шахом ответим — пусть соперник думает о короле!', stages: [3, 5] },
  ],

  'v3.danger.letGo.stronger': [
    // stages 1–2: the plainest words (спасать, ход, сильнее, важнее) — two of 5 words besides the shared 5-word one
    { t: 'Не спасаем — есть ход важнее!', stages: [1, 2] },
    { t: 'Сильный ход сейчас важнее спасения!', stages: [1, 2] },
    { t: 'Спасать не будем: у нас ход сильнее!', stages: [1, 2] },
    { t: 'Тут спасать не надо — у нас ход похитрее!', stages: [1, 2] },
    { t: 'Смелее: можно не спасать, есть ход сильнее!', stages: [1, 2] },
    // every stage
    { t: 'Можно и не спасать — есть ход сильнее!' },
    { t: 'Есть ход сильнее, чем спасать.' },
    { t: 'Спасать не обязательно: я нашёл ход посильнее!' },
    { t: 'Не спешим спасать — сначала сильный ход!' },
    { t: 'Пусть стоит — у нас дело поважнее!' },
    { t: 'А у нас есть кое-что посильнее!' },
    { t: 'Спокойно: у нас найдётся ход поинтереснее!' },
    { t: 'Я вижу ход, который важнее спасения!' },
    { t: 'Оставим как есть — сейчас нужен другой ход!' },
    // stages 3–5: «угроза», «идея», «план», «ответ покрепче», «такой случай» — the older child's words
    { t: 'Не будем убегать — у нас есть идея получше!', stages: [3, 5] },
    { t: 'Иногда можно не спасать — сейчас как раз такой случай.', stages: [3, 5] },
    { t: 'Забудем пока про спасение — есть ход мощнее!', stages: [3, 5] },
    { t: 'На угрозу пока не отвечаем — есть ход сильнее!', stages: [3, 5] },
    { t: 'Спасение подождёт — у нас ход с сюрпризом!', stages: [3, 5] },
    { t: 'Тут можно смело не спасать — у нас есть ответ покрепче!', stages: [3, 5] },
    { t: 'Мы не будем защищаться — у нас план похитрее!', stages: [3, 5] },
    { t: 'Вместо защиты — ход ещё сильнее!', stages: [3, 5] },
    { t: 'Защищаться некогда — есть ход мощнее!', stages: [3, 5] },
  ],

  // the mate cannot be stopped: after any lead («Давай сходим {конём}»), honest and without a promise
  'v3.danger.lastStand': [
    { t: '— сыграем до конца!' },
    { t: '— не сдаёмся до последнего хода!' },
    { t: '— держимся до самого конца!' },
    { t: '— сражаемся до последнего!' },
    { t: '— доиграем партию достойно!' },
    { t: '— настоящие шахматисты играют до конца!' },
    { t: '— сдаваться не будем!' },
    { t: '— играем, пока партия не закончилась!' },
    { t: '— стоим до конца, как герои!' },
    { t: '— бороться нужно до последнего хода!' },
  ],

  // ───────────────────────── treasure: the child finds the move (the line never tells it) ─────────────────────────

  'v3.treasure.mate': [
    { t: 'Ого, тут можно поставить мат!' },
    { t: 'Королю соперника можно поставить мат!' },
    { t: 'Подарок: где-то рядом прячется мат!' },
    { t: 'Я вижу мат для короля соперника!' },
    { t: 'Тс-с, тут спрятан мат!' },
    { t: 'Ух ты, тут мат!' },
    { t: 'Король соперника в ловушке — есть мат!' },
    { t: 'Смотри-ка, у нас есть мат!' },
    { t: 'Большой подарок — мат королю соперника!' },
    { t: 'Вот так удача — на доске есть мат!' },
  ],

  'v3.treasure.free': [
    { t: 'Ого, {конь} соперника стоит без защиты!' },
    { t: 'Подарок: {коня} соперника никто не охраняет!' },
    { t: 'Смотри, за {коня} соперника никто не заступится!' },
    { t: 'Без защиты {p:остался|осталась} {конь} соперника!' },
    { t: 'Соперник не прикрыл {коня}!' },
    { t: 'Можно бесплатно съесть {коня} соперника!' },
    { t: 'У соперника {конь} без защиты — это подарок!' },
    { t: 'Эй, а {конь} соперника-то без охраны!' },
    { t: 'Ух ты, {коня} соперника некому защитить!' },
    { t: 'Вижу подарок: {конь} соперника {p:брошен|брошена} без защиты!' },
    { t: 'Кажется, соперник проглядел {коня}!' },
  ],

  'v3.treasure.win': [
    { t: 'Ого, {коня} соперника можно выгодно забрать!' },
    { t: '{Коня} соперника защищают, но съесть {его} всё равно выгодно!' },
    { t: 'Можно съесть {коня} соперника и остаться в выигрыше!' },
    { t: 'Тут выгодное взятие: {конь} соперника!' },
    { t: 'Подарок: {коня} соперника выгодно съесть, даже с защитой!' },
    { t: 'Забрать {коня} соперника — нам в плюс!' },
    { t: 'Кажется, {коня} соперника можно забрать с выгодой!' },
    { t: 'У {коня:gen} соперника есть защита, но она не спасёт!' },
  ],

  'v3.treasure.fork': [
    { t: 'Ничего себе: можно напасть сразу на двоих!' },
    { t: 'Подарок: один ход — и под ударом сразу двое!' },
    { t: 'Можно атаковать двоих одним ходом!' },
    { t: 'Тут прячется ход, который пугает сразу двоих!' },
    { t: 'Смотри, тут есть двойной удар!', stages: [1, 2] },
    { t: 'Ага, двойной удар на доске!', stages: [1, 2] },
    { t: 'Двойной удар ждёт нас!', stages: [1, 2] },
    { t: 'Здесь можно сделать вилку!', stages: [3, 5] },
    { t: 'Вилка на доске — какой подарок!', stages: [3, 5] },
    { t: 'Кажется, я вижу вилку!', stages: [3, 5] },
  ],

  'v3.treasure.tactic': [
    { t: 'Опа, тут спрятан сильный удар!' },
    { t: 'Смотри-ка, тут есть хитрый приём!' },
    { t: 'Я вижу сильный ход — он тут прячется!' },
    { t: 'Подарок: у нас есть мощный удар!' },
    { t: 'Здесь можно здорово ударить!' },
    { t: 'Тут можно кое-что выиграть хитрым ходом!' },
    { t: 'Вот так позиция — у нас есть ловкий ход!' },
  ],

  'v3.treasure.hunt': [
    { t: 'Где-то тут спрятан подарок…' },
    { t: 'Чую подарок, но где же он…' },
    { t: 'Кажется, на доске кое-что припрятано…' },
    { t: 'Тс-с, я кое-что заметил на доске…' },
    { t: 'У меня секрет: на доске есть подарок.' },
    { t: 'Хм, тут пахнет подарком…' },
    { t: 'Где-то на доске нас ждёт сюрприз…' },
    { t: 'Ищем клад: на доске есть подарок…' },
  ],

  'v3.treasure.ask': [
    { t: 'Найдёшь {g:сам|сама}?' },
    { t: 'Сможешь найти?' },
    { t: 'Поищешь?' },
    { t: 'Кто первый найдёт — ты или я?' },
    { t: 'Попробуешь отыскать?' },
    { t: 'Справишься без подсказки?' },
    { t: 'Видишь, что тут можно сделать?' },
    { t: 'Догадаешься, какой ход?' },
    { t: 'Посмотри внимательно — где подарок?' },
    { t: 'Сумеешь разглядеть?' },
    { t: 'Поиграем в прятки — найди, где подарок!' },
    { t: 'Ну-ка, найди {g:сам|сама}!' },
    { t: 'Найдёшь, каким ходом?' },
    { t: 'Раскроешь секрет?' },
    { t: 'Включай глаза сыщика — найдёшь?' },
    { t: 'Какой ход тут прячется, как думаешь?' },
    { t: 'Покажешь мне этот ход?' },
    { t: 'Найдёшь раньше, чем я подскажу?' },
  ],

  // ───────────────────────── other moments ─────────────────────────

  'v3.hurry': [
    { t: 'Поторопись!' },
    { t: 'Давай побыстрее!' },
    { t: 'Ходим шустрее!' },
    { t: 'Скорее, скорее!' },
    { t: 'Чуть быстрее, ладно?' },
    { t: 'Поспешим — думаем быстро!' },
    { t: 'Пора ходить!' },
    { t: 'Ну-ка, быстренько!' },
    { t: 'Не задерживаемся!' },
    { t: 'Выбирай ход поскорее!' },
  ],
};
